import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// shadcn classname helper
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}


// ---------- Image watermark (Cloudinary delivery transformation) ----------
// Applies a clearly visible "© fodindin.com" overlay at the bottom-right of
// every Cloudinary-hosted image. Non-Cloudinary URLs (Unsplash seeds, data
// URLs, blob URLs) are returned untouched so the watermark only protects
// user-uploaded content.
//
// Transformation:
//   l_text:Arial_26_bold:© fodindin.com   → bold overlay text, size 26
//   co_white                               → white fill
//   o_85                                   → 85% opacity (clearly visible)
//   g_south_east, x_22, y_18               → bottom-right with padding
//   e_shadow:50                            → soft drop shadow → readable on
//                                            both light and dark images
//
// Single bottom-right mark, scaled to the image (~30% of width), slightly transparent but readable.
const _WATERMARK_TX = "l_text:Arial_60_bold:FODINDIN.NET,co_white,o_70,c_scale,w_0.3,fl_relative,g_south_east,x_0.03,y_0.03,fl_relative,e_shadow:60";
const _WM_PROXY = `${process.env.REACT_APP_BACKEND_URL}/api/img/wm?u=`;

export function withWatermark(url) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:") || url.startsWith("blob:") || url.startsWith("/")) return url;
  if (url.includes("/api/img/wm?u=")) return url;
  // Non-Cloudinary hosts (stock/demo photos): server-side watermark proxy.
  if (!url.includes("res.cloudinary.com")) return url.startsWith("https://") ? `${_WM_PROXY}${encodeURIComponent(url)}` : url;
  // Skip if a watermark is already in the URL (idempotent).
  if (url.includes("l_text:Arial_")) return url;
  const marker = "/upload/";
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  const head = url.slice(0, idx + marker.length);
  const tail = url.slice(idx + marker.length);
  return `${head}${_WATERMARK_TX}/${tail}`;
}


// Vehicules — types d'energie (valeur stockee -> libelle)
export const ENERGIES = [
  { key: "hybride", label: "Hybride" },
  { key: "electrique", label: "Electrique" },
  { key: "gpl", label: "GPL" },
  { key: "diesel", label: "Diesel" },
  { key: "essence", label: "Essence" },
];
export const energieLabel = (key) => ENERGIES.find((e) => e.key === key)?.label || "";

export const BOITES = [
  { key: "manuelle", label: "Manuelle" },
  { key: "automatique", label: "Automatique" },
];
export const boiteLabel = (key) => BOITES.find((b) => b.key === key)?.label || "";
export const VEHICULE_MARQUES = [
  "Toyota", "Mercedes-Benz", "Hyundai", "Kia", "Peugeot", "Renault", "Nissan", "Honda",
  "BMW", "Volkswagen", "Ford", "Suzuki", "Mitsubishi", "Land Rover", "Lexus", "Audi",
  "Chevrolet", "Mazda", "Citroën", "Isuzu", "Jeep", "Porsche", "Autre",
];
export const fmtKm = (km) => (km == null || km === "" ? "" : `${Number(km).toLocaleString("fr-FR")} km`);


export const CATEGORIES = [
  { key: "Immobilier", code: "IM", desc: "Ventes, biens neufs, locations, colocations, bureaux, commerces et services." },
  { key: "Hotelleries", code: "HT", desc: "Chambres libres, offres optimisees, prix competitifs et contrats hoteliers." },
  { key: "Vehicules", code: "VH", desc: "Voitures, motos, utilitaires, location auto et accessoires." },
  { key: "Equipement Auto", code: "EA", desc: "Pieces detachees, pneus, jantes, batteries, audio, entretien et accessoires auto." },
  { key: "Mode", code: "MO", desc: "Vetements et chaussures pour femme, homme, enfant et maternite." },
  { key: "Montres & Bijoux", code: "MB", desc: "Montres, bagues, colliers, bracelets — femme, homme, enfant et mixte." },
  { key: "Accessoires & Bagagerie", code: "AB", desc: "Sacs, ceintures, casquettes, valises et bagages." },
  { key: "Emploi", code: "EM", desc: "Offres d'emploi : nounou, menage, cuisine, gardiennage, securite, CDI/CDD, stages." },
  { key: "Formation", code: "FO", desc: "Ecoles, instituts, formations professionnelles, cours et certifications." },
  { key: "Entreprise", code: "EN", desc: "Espace B2B : cession de fonds de commerce, appels d'offres, sous-traitance, services aux entreprises, investisseurs." },
  { key: "Electronique", code: "EL", desc: "Smartphones, ordinateurs, TV, gaming et objets connectes." },
  { key: "Materiel pro", code: "MP", desc: "BTP, restauration, industrie, bureau et outillage professionnel." },
  { key: "Mobiliers", code: "MO2", desc: "Salon, chambre, bureau, rangements et deco maison." },
  { key: "Loisir", code: "LS", desc: "Sport, jeux, musique, evenements et activites de detente." },
];

// Sub-categories per top-level (for Mode universe)
export const SUBCATEGORIES = {
  "Emploi": {
    "Services a domicile": ["Nounou / garde d'enfants", "Nettoyage / menage", "Cuisine", "Aide aux personnes agees", "Chauffeur particulier"],
    "Gardiennage & securite": ["Gardien de residence", "Gardien de batiment", "Agent de securite centre commercial", "Vigile de nuit"],
    "Autres offres": ["CDI / CDD", "Stage", "Freelance / mission", "Commercial / vente"],
  },
  "Formation": {
    "Ecoles & instituts": ["Ecole primaire / secondaire", "Universite / grande ecole", "Institut professionnel"],
    "Formations professionnelles": ["Informatique & bureautique", "Comptabilite & gestion", "Hotellerie & restauration", "Couture & mode", "Coiffure & esthetique", "Conduite / permis", "Langues"],
    "Cours & soutien": ["Soutien scolaire", "Cours particuliers", "Formation en ligne", "Certification"],
  },
  "Entreprise": {
    "Cession & reprise": ["Fonds de commerce", "Parts sociales / societe", "Franchise", "Materiel & stock d'entreprise"],
    "Appels d'offres & marches": ["Appel d'offres", "Sous-traitance", "Partenariat commercial", "Distribution / representation"],
    "Services aux entreprises": ["Comptabilite & juridique", "Logistique & transport", "Informatique & digital", "Marketing & communication", "Import-export", "Nettoyage & securite pro"],
    "Investissement": ["Recherche d'investisseurs", "Levee de fonds", "Immobilier d'entreprise", "Co-entreprise / joint-venture"],
  },
  "Mode": {
    "Vetements": ["Femme", "Maternite", "Homme", "Enfant"],
    "Chaussures": ["Femme", "Homme", "Enfant"],
  },
  "Montres & Bijoux": {
    "Montres & Bijoux": ["Femme", "Homme", "Enfant", "Mixte"],
  },
  "Accessoires & Bagagerie": {
    "Accessoires & Bagagerie": ["Femme", "Homme", "Enfant"],
  },
  "Immobilier": {
    "Vente": ["Sans travaux", "Avec travaux", "Inachevee"],
    "Location": ["Maison meublee", "Maison non-meublee", "Appartement", "Studio"],
    "Terrain": ["Avec documents", "Sans documents", "Location ville", "Location agricole"],
  },
  "Equipement Auto": {
    "Pneus & Jantes": ["Pneus", "Jantes", "Accessoires roues"],
    "Pieces moteur": ["Pieces moteur", "Filtres", "Batteries", "Freinage"],
    "Audio & GPS": ["Autoradio & Enceintes", "GPS & Dashcam"],
    "Entretien": ["Huiles & Liquides", "Nettoyage & Cosmetique auto", "Outillage"],
  },
  "Vehicules": {
    "Autos": ["Voitures", "Utilitaires & Camions", "Location auto"],
    "Deux-roues": ["Motos & Scooters", "Tricycles"],
  },
};

export const MOTO_SUBCATS = ["Motos & Scooters", "Tricycles"];
export const isMoto = (sub) => MOTO_SUBCATS.includes(sub);
export const MOTO_ENERGIES = ENERGIES.filter((e) => ["essence", "diesel", "electrique"].includes(e.key));
export const CYLINDREES = [
  { key: "50", label: "50 cc" },
  { key: "125", label: "125 cc" },
  { key: "250+", label: "250 cc et plus" },
];
export const cylindreeLabel = (key) => CYLINDREES.find((c) => c.key === key)?.label || "";
export const MOTO_MARQUES = [
  "Yamaha", "Apsonic", "Haojue", "TVS", "KTM", "American Ironhorse", "Aprilia", "Arc", "Arctic Cat", "Atul", "Autodex",
  "Bajaj", "Benelli", "Beth", "Bir", "BMW", "Boss Hoss", "Can-Am", "CFMoto", "Cushman", "Denstar", "Ducati", "GPX", "Haojin",
  "Harley-Davidson", "Honda", "Husqvarna", "Jonway", "Kawasaki", "Kibo", "KR Motors", "Kymco", "Lifan", "Max-S", "Moto Guzzi",
  "Peugeot", "Piaggio", "Raleigh", "Revoo", "Royal", "Royal Enfield", "Sanya", "Senke", "Sonlink", "Speeder", "Strong 1",
  "Super Gallant", "Suzuki", "SYM", "Tailg", "Tekken", "Tricycle", "Vespa", "Voge", "Wottan Motor", "Yadea", "Zontes", "Autre",
];

// Tooltip / hint per sub-category. Used in DeposerPage AND ImmobilierPage.
export const SUBCATEGORY_HINTS = {
  "Motos & Scooters": "Yamaha, Apsonic, Haojue, TVS, KTM… Precisez la marque, la cylindree, le carburant (essence, diesel, electrique) et la transmission (automatique ou manuelle).",
  "Tricycles": "Tricycles de transport de marchandises ou de passagers. Indiquez la charge utile et l'etat.",
  // Mode-style
  "Femme": "Articles pour femmes adultes.",
  "Homme": "Articles pour hommes adultes.",
  "Enfant": "Articles pour enfants et bebes.",
  "Mixte": "Articles unisexes ou pour adultes (homme ou femme).",
  "Maternite": "Vetements adaptes a la grossesse.",
  // Immobilier - Vente
  "Sans travaux": "Bien neuf ou recemment renove, pret a habiter sans travaux a prevoir.",
  "Avec travaux": "Bien necessitant des renovations, finitions ou modernisation.",
  "Inachevee": "Construction commencee mais non terminee. Finition a la charge de l'acheteur.",
  // Immobilier - Location
  "Maison meublee": "Maison meublee, prete pour un emmenagement immediat ou court sejour.",
  "Maison non-meublee": "Maison nue, location longue duree. Locataire amenage lui-meme.",
  "Appartement": "Appartement en immeuble. Specifiez le nombre de pieces dans la description.",
  "Studio": "Petit logement d'une seule piece, ideal pour celibataire ou etudiant.",
  // Immobilier - Terrain
  "Avec documents": "Terrain avec titre foncier ou documents cadastraux valides.",
  "Sans documents": "Terrain sans documents officiels — situation juridique a clarifier.",
  "Location ville": "Terrain urbain ou peri-urbain a louer (residentiel ou commercial).",
  "Location agricole": "Terrain rural a louer pour exploitation agricole, plantation ou elevage.",
  // Equipement Auto
  "Pneus": "Pneus neufs ou occasion — precisez dimensions et etat dans la description.",
  "Jantes": "Jantes alu ou tole — precisez le diametre et l'entraxe.",
  "Accessoires roues": "Enjoliveurs, ecrous, capteurs de pression et accessoires de roues.",
  "Pieces moteur": "Pieces mecaniques : moteur, boite, embrayage, alternateur, etc.",
  "Filtres": "Filtres a huile, a air, a carburant et d'habitacle.",
  "Batteries": "Batteries auto neuves ou reconditionnees — precisez l'amperage.",
  "Freinage": "Plaquettes, disques, etriers et kits de freinage.",
  "Autoradio & Enceintes": "Autoradios, ecrans multimedia, enceintes et amplis.",
  "GPS & Dashcam": "Navigateurs GPS, cameras embarquees et trackers.",
  "Huiles & Liquides": "Huiles moteur, liquides de frein et de refroidissement.",
  "Nettoyage & Cosmetique auto": "Produits de lavage, polish et entretien esthetique.",
  "Outillage": "Crics, cles, coffrets et outillage mecanique.",
};

export const CITIES = [
  // Grandes villes
  "Abidjan", "Bouake", "Yamoussoukro", "San Pedro", "Korhogo",
  "Daloa", "Man", "Gagnoa", "Abengourou", "Bondoukou", "Divo", "Soubre", "Odienne",
  // Localités à fort potentiel foncier (axe lagunaire & littoral)
  "Grand-Bassam", "Assinie", "Assouinde", "Mondoukou",
  "Jacqueville", "Dabou", "Bingerville",
  "Ile Boulay", "Ile Vitre",
  "Bregbo", "Eloka-Te", "Asseoufoue", "Motobe",
  "Yaou", "Attinguie", "Samo", "Sassako-Begniny",
];

export const COCODY_QUARTIERS = [
  "Agban Gendarmerie", "Aghien", "Akouédo Ancien", "Akouédo Nouveau", "Akouédo Village",
  "Allabra Sogephia", "Ambassade", "Angré", "Anono", "Attoban", "Blockhauss", "Bonoumin",
  "Canebière", "Centre", "CHU", "Cité des Arts", "Cité des Cadres", "Cocody Village",
  "Copraci Coprim", "Danga", "II Plateaux 1 AE", "II Plateaux Est", "II Plateaux 2",
  "Djorogobité", "E.N.A", "École de Gendarmerie", "E.E.C.I Riviera", "Lycée Technique",
  "M'badon", "M'pouto", "Opération Palmeraie Génie 2000", "Plateau Dokui", "Riviera Golf 1",
  "Riviera Golf 2", "Riviera 3-4-5", "Riviera Palmeraie", "SIDECI Riviera", "SIDECI Zoo",
  "Sogephia Riviera II", "Sopim Valon", "Université", "Val Doyen", "Plaingué",
  "7ème Tranche", "8ème Tranche", "9ème Tranche",
];

export const ABIDJAN_QUARTIERS = [
  "Cocody", "Riviera", "2 Plateaux", "Angre", "Palmeraie", "Marcory", "Bietry",
  "Zone 4", "Plateau", "Treichville", "Yopougon", "Abobo", "Adjame", "Attiecoube",
  "Koumassi", "Port-Bouet", "Vridi", "Bingerville", "Anyama", "Songon",
  ...COCODY_QUARTIERS.map((q) => `Cocody - ${q}`),
];

export const XOF_PER_EUR = 655.957;
export const fmtEur = (xof) => `≈ ${new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format((Number(xof) || 0) / XOF_PER_EUR)}`;

export const fmtPrice = (price, unit = "") => {
  const formatted = new Intl.NumberFormat("fr-FR").format(price);
  return `${formatted} FCFA${unit ? " " + unit : ""}`;
};

// favorites in localStorage
const FAV_KEY = "fodindin:favorites";
export const getFavorites = () => {
  try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); }
  catch { return []; }
};
export const toggleFavorite = (id) => {
  const favs = getFavorites();
  const idx = favs.indexOf(id);
  if (idx >= 0) favs.splice(idx, 1);
  else favs.push(id);
  localStorage.setItem(FAV_KEY, JSON.stringify(favs));
  return favs;
};
export const isFavorite = (id) => getFavorites().includes(id);

// Draft for deposer form
// Stored as { saved_at_ms, user_id, data } so we can:
//   - expire stale drafts (TTL_MS, default 6 h)
//   - prevent one user from seeing another user's draft on a shared device
//   - safely clear all drafts on logout
const DRAFT_KEY = "fodindin:draft";
const DRAFT_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

export const saveDraft = (draft, userId = null) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      saved_at_ms: Date.now(),
      user_id: userId || null,
      data: draft,
    }));
  } catch { /* quota / private mode */ }
};

export const loadDraft = (userId = null) => {
  try {
    const raw = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
    if (!raw) return null;
    // Back-compat: if the stored value is the old flat shape (no `data`/`saved_at_ms`),
    // discard it. Safer than restoring an old user's content.
    if (typeof raw.saved_at_ms !== "number" || !raw.data) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    // TTL: drop expired drafts
    if (Date.now() - raw.saved_at_ms > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    // User isolation: drop drafts that belong to a different user.
    // Allow anonymous-saved drafts (user_id=null) to be restored once the user
    // logs in — common flow when a guest fills the form, then signs in to publish.
    if (raw.user_id && userId && raw.user_id !== userId) {
      return null; // do NOT delete — the other user might log back on
    }
    return { ...raw.data, _saved_at_ms: raw.saved_at_ms, _user_id: raw.user_id };
  } catch {
    return null;
  }
};

export const clearDraft = () => {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
};
